/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  collaborationService,
  type FormUpdate,
} from "../services/collaborationService";
import toast from "react-hot-toast";

export interface UseCollaborativeFormOptions<T = any> {
  formType: FormUpdate["formType"];
  formId: string;
  onUpdate?: (data: T, updatedBy?: FormUpdate["updatedBy"]) => void;
  enablePolling?: boolean;
  pollingInterval?: number;
  showUpdateNotifications?: boolean;
  mergeStrategy?: "replace" | "merge" | "custom";
  customMerge?: (current: T, update: T) => T;
}

export interface CollaborativeFormState<T = any> {
  data: T | null;
  isUpdating: boolean;
  lastUpdate: FormUpdate<T> | null;
  hasRemoteUpdates: boolean;
  updateCount: number;
  isConnected: boolean;
}

/**
 * Custom hook for managing collaborative form state with real-time updates
 * @param initialData - Initial form data
 * @param options - Configuration options for the collaborative form
 * @returns Collaborative form state and control functions
 */
export function useCollaborativeForm<T = any>(
  initialData: T | null,
  options: UseCollaborativeFormOptions<T>
) {
  const {
    formType,
    formId,
    onUpdate,
    enablePolling = true,
    pollingInterval = 5000,
    showUpdateNotifications = true,
    mergeStrategy = "merge",
    customMerge,
  } = options;

  const [state, setState] = useState<CollaborativeFormState<T>>({
    data: initialData,
    isUpdating: false,
    lastUpdate: null,
    hasRemoteUpdates: false,
    updateCount: 0,
    isConnected: false,
  });

  const [updateAnimation, setUpdateAnimation] = useState(false);
  const stopPollingRef = useRef<(() => void) | null>(null);
  const toastIdRef = useRef<string | null>(null);

  /**
   * Merge incoming updates with current data based on merge strategy
   */
  const mergeData = useCallback(
    (currentData: T | null, updateData: T): T => {
      if (!currentData) return updateData;

      switch (mergeStrategy) {
        case "replace":
          return updateData;
        case "custom":
          return customMerge
            ? customMerge(currentData, updateData)
            : updateData;
        case "merge":
        default:
          // Deep merge for objects, replace for primitives
          if (
            typeof currentData === "object" &&
            typeof updateData === "object"
          ) {
            return { ...currentData, ...updateData };
          }
          return updateData;
      }
    },
    [mergeStrategy, customMerge]
  );

  /**
   * Handle incoming updates from the collaboration service
   */
  const handleFormUpdate = useCallback(
    (update: FormUpdate<T>) => {
      setState((prev) => {
        const mergedData = mergeData(prev.data, update.data);

        // Show update notification
        if (showUpdateNotifications && update.updatedBy) {
          // Clear previous toast if exists
          if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
          }

          // Create new toast with update message
          const message = `📝 Loaded latest entries from ${update.updatedBy.userName || "other lab member"}`;
          toastIdRef.current = toast.success(message, {
            duration: 4000,
            position: "bottom-right",
            style: {
              background: "#10B981",
              color: "#fff",
              padding: "12px 20px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            },
            icon: "✨",
          }) as string;
        }

        // Trigger update animation
        setUpdateAnimation(true);
        setTimeout(() => setUpdateAnimation(false), 1000);

        // Call onUpdate callback if provided
        if (onUpdate) {
          onUpdate(mergedData, update.updatedBy);
        }

        return {
          ...prev,
          data: mergedData,
          lastUpdate: update,
          hasRemoteUpdates: true,
          updateCount: prev.updateCount + 1,
          isUpdating: false,
        };
      });
    },
    [mergeData, showUpdateNotifications, onUpdate]
  );

  /**
   * Start polling for updates
   */
  const startPolling = useCallback(() => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
    }

    stopPollingRef.current = collaborationService.startPolling(
      formType,
      formId,
      handleFormUpdate,
      pollingInterval
    );

    setState((prev) => ({
      ...prev,
      isConnected: true,
    }));
  }, [formType, formId, handleFormUpdate, pollingInterval]);

  /**
   * Stop polling for updates
   */
  const stopPolling = useCallback(() => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }

    setState((prev) => ({ ...prev, isConnected: false }));
  }, []);

  /**
   * Manually fetch latest updates (for refresh button)
   */
  const fetchLatestUpdates = useCallback(async () => {
    console.log(
      `[useCollaborativeForm] Manual refresh triggered for ${formType}_${formId}`
    );
    setState((prev) => ({ ...prev, isUpdating: true }));

    try {
      // Use manual refresh method to force a single update check
      const updates = await collaborationService.manualRefresh<T>(
        formType,
        formId
      );

      if (updates.hasUpdates && updates.updates) {
        const mergedData = mergeData(state.data, updates.updates.data);

        setState((prev) => ({
          ...prev,
          data: mergedData,
          isUpdating: false,
          hasRemoteUpdates: false,
          lastUpdate: updates.updates || null,
          updateCount: prev.updateCount + 1,
        }));

        if (showUpdateNotifications) {
          toast.success("✅ Refreshed successfully!", {
            duration: 3000,
            position: "bottom-right",
          });
        }

        // Trigger update callback if provided
        if (onUpdate && updates.updates.updatedBy) {
          onUpdate(mergedData, updates.updates.updatedBy);
        }
      } else {
        setState((prev) => ({ ...prev, isUpdating: false }));

        if (showUpdateNotifications) {
          toast.success("✅ Already up to date", {
            duration: 2000,
            position: "bottom-right",
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch latest updates:", error);
      setState((prev) => ({ ...prev, isUpdating: false }));

      if (showUpdateNotifications) {
        toast.error("❌ Refresh failed", {
          position: "bottom-right",
        });
      }
    }
  }, [
    formType,
    formId,
    showUpdateNotifications,
    mergeData,
    state.data,
    onUpdate,
  ]);
  /**
   * Update local data (for optimistic updates)
   */
  const updateLocalData = useCallback(
    (updater: (prev: T | null) => T | null) => {
      setState((prev) => ({
        ...prev,
        data: updater(prev.data),
      }));
    },
    []
  );

  /**
   * Reset remote update flag
   */
  const acknowledgeUpdates = useCallback(() => {
    setState((prev) => ({ ...prev, hasRemoteUpdates: false }));
  }, []);

  // Start/stop polling based on enablePolling flag and formId changes
  useEffect(() => {
    if (enablePolling) {
      console.log(
        `[useCollaborativeForm] Starting polling for ${formType}_${formId}`
      );
      startPolling();
    } else {
      console.log(
        `[useCollaborativeForm] Polling disabled for ${formType}_${formId}`
      );
    }

    return () => {
      console.log(
        `[useCollaborativeForm] Cleanup - stopping polling for ${formType}_${formId}`
      );
      stopPolling();
    };
  }, [enablePolling, formType, formId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup toasts on unmount
  useEffect(() => {
    return () => {
      if (toastIdRef.current) {
        console.log(
          `[useCollaborativeForm] Cleanup - dismissing toast for ${formType}_${formId}`
        );
        toast.dismiss(toastIdRef.current);
      }
    };
  }, []);

  return {
    ...state,
    updateAnimation,
    startPolling,
    stopPolling,
    fetchLatestUpdates,
    updateLocalData,
    acknowledgeUpdates,
  };
}
